import React, { useState } from 'react';
import { StaffRole } from '../types';
import { useApp } from '../context/AppContext';
import {
  Lock,
  KeyRound,
  Mail,
  Eye,
  EyeOff,
  AlertCircle,
  Shield,
  ChefHat,
  UserCheck,
  ArrowLeft
} from 'lucide-react';
import { playModernSuccessChime } from '../utils/audio';

export const StaffAuthGate: React.FC = () => {
  const { loginStaff, authenticateStaffUser, setCurrentView } = useApp();
  const [authMode, setAuthMode] = useState<'pin' | 'password'>('pin');
  const [pin, setPin] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handlePinDigit = (digit: string) => {
    if (pin.length < 4) {
      const nextPin = pin + digit;
      setPin(nextPin);
      setErrorMsg('');
      if (nextPin.length === 4) {
        verifyPin(nextPin);
      }
    }
  };

  const handlePinBackspace = () => {
    setPin(prev => prev.slice(0, -1));
    setErrorMsg('');
  };

  const handlePinClear = () => {
    setPin('');
    setErrorMsg('');
  };

  const verifyPin = async (pinToTest: string) => {
    setIsLoading(true);
    setErrorMsg('');
    try {
      const res = await authenticateStaffUser({
        login_type: 'pin',
        pin_code: pinToTest
      });

      if (res.success && res.user) {
        playModernSuccessChime();
        loginStaff(res.user.role as StaffRole, res.user);
        setPin('');
      } else {
        setErrorMsg(res.error || 'Virheellinen PIN-koodi / Invalid PIN code');
        setPin('');
      }
    } catch (e: any) {
      setErrorMsg('Authentication failed: ' + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      setErrorMsg('Syötä sähköposti/puhelin ja salasana / Enter email/mobile & password');
      return;
    }

    setIsLoading(true);
    setErrorMsg('');
    try {
      const res = await authenticateStaffUser({
        login_type: 'password',
        identifier: identifier.trim(),
        password: password
      });

      if (res.success && res.user) {
        playModernSuccessChime();
        loginStaff(res.user.role as StaffRole, res.user);
        setPassword('');
        setIdentifier('');
      } else {
        setErrorMsg(res.error || 'Virheelliset kirjautumistiedot / Invalid credentials');
      }
    } catch (e: any) {
      setErrorMsg('Login failed: ' + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '75vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px 16px'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '430px',
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--border-medium)',
          boxShadow: 'var(--shadow-xl)',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div
          style={{
            background: 'var(--bg-secondary)',
            padding: '24px 24px 18px 24px',
            borderBottom: '1px solid var(--border-subtle)',
            textAlign: 'center'
          }}
        >
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'rgba(224, 90, 54, 0.12)',
              color: 'var(--accent-copper)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px auto'
            }}
          >
            <Lock size={26} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.35rem', fontWeight: 800, margin: '0 0 6px 0' }}>
            Staff & Admin Authentication
          </h2>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>
            Kirjaudu sisään päästäksesi hallintapaneeliin / Enter PIN or credentials to access dashboard
          </p>
        </div>

        {/* Auth Mode Tabs */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            background: 'var(--bg-secondary)',
            padding: '6px',
            borderBottom: '1px solid var(--border-subtle)'
          }}
        >
          <button
            type="button"
            onClick={() => { setAuthMode('pin'); setErrorMsg(''); }}
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: authMode === 'pin' ? 'var(--bg-card)' : 'transparent',
              color: authMode === 'pin' ? 'var(--accent-copper)' : 'var(--text-muted)',
              fontWeight: 800,
              fontSize: '0.84rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              boxShadow: authMode === 'pin' ? '0 2px 8px rgba(0,0,0,0.15)' : 'none'
            }}
          >
            <KeyRound size={15} />
            <span>4-Digit PIN</span>
          </button>

          <button
            type="button"
            onClick={() => { setAuthMode('password'); setErrorMsg(''); }}
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: authMode === 'password' ? 'var(--bg-card)' : 'transparent',
              color: authMode === 'password' ? 'var(--accent-copper)' : 'var(--text-muted)',
              fontWeight: 800,
              fontSize: '0.84rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              boxShadow: authMode === 'password' ? '0 2px 8px rgba(0,0,0,0.15)' : 'none'
            }}
          >
            <Mail size={15} />
            <span>Email / Password</span>
          </button>
        </div>

        {/* Error Alert */}
        {errorMsg && (
          <div
            style={{
              margin: '16px 20px 0 20px',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'rgba(244, 63, 94, 0.12)',
              border: '1px solid var(--accent-rose)',
              color: 'var(--accent-rose)',
              fontSize: '0.82rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            <span>{errorMsg}</span>
          </div>
        )}

        <div style={{ padding: '24px 20px' }}>
          {/* TAB 1: PIN Pad */}
          {authMode === 'pin' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
                Syötä 4-numeroinen PIN-koodi
              </div>

              {/* PIN Bubbles Display */}
              <div style={{ display: 'flex', gap: '14px', margin: '4px 0 10px 0' }}>
                {[0, 1, 2, 3].map(idx => (
                  <div
                    key={idx}
                    style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      border: `2px solid ${pin.length > idx ? 'var(--accent-copper)' : 'var(--border-medium)'}`,
                      background: pin.length > idx ? 'var(--accent-copper)' : 'transparent',
                      transition: 'all 0.15s ease'
                    }}
                  />
                ))}
              </div>

              {/* Keypad */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', width: '100%', maxWidth: '280px' }}>
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '⌫'].map(btn => {
                  const isAction = btn === 'C' || btn === '⌫';
                  return (
                    <button
                      type="button"
                      key={btn}
                      disabled={isLoading}
                      onClick={() => {
                        if (btn === 'C') handlePinClear();
                        else if (btn === '⌫') handlePinBackspace();
                        else handlePinDigit(btn);
                      }}
                      style={{
                        padding: '16px 0',
                        fontSize: isAction ? '1rem' : '1.35rem',
                        fontWeight: 800,
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid var(--border-subtle)',
                        background: isAction ? 'var(--bg-secondary)' : 'var(--bg-surface)',
                        color: isAction ? 'var(--accent-copper)' : 'var(--text-primary)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.1s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                      }}
                      onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.94)')}
                      onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      {btn}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 2: Email / Password */}
          {authMode === 'password' && (
            <form onSubmit={handlePasswordLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Sähköposti tai Puhelin / Email or Mobile</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    required
                    className="form-input"
                    value={identifier}
                    onChange={e => setIdentifier(e.target.value)}
                    placeholder="admin@theovenstory.fi"
                    style={{ paddingLeft: '36px' }}
                  />
                  <Mail
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)'
                    }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Salasana / Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="form-input"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter password..."
                    style={{ paddingLeft: '36px', paddingRight: '38px' }}
                  />
                  <Lock
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading}
                style={{ width: '100%', padding: '12px', fontSize: '0.94rem', marginTop: '6px' }}
              >
                {isLoading ? 'Tarkistetaan...' : 'Kirjaudu Sisään / Sign In'}
              </button>
            </form>
          )}

          {/* Back to Customer Menu */}
          <div style={{ marginTop: '20px', textAlign: 'center', borderTop: '1px solid var(--border-subtle)', paddingTop: '16px' }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCurrentView('customer')}
              style={{ fontSize: '0.82rem', padding: '6px 14px', borderRadius: 'var(--radius-full)' }}
            >
              <ArrowLeft size={14} />
              <span>Palaa asiakasvalikkoon / Back to Menu</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
